<template>
  <div class="ctas">
    <div class="ctas__list flex flex-col lg:flex-row flex-wrap gap-4">
      <div class="ctas__item" v-for="(cta, index) in component.ctas" :key="index">
        <SharedButton
          class="ctas__item-button w-max"
          :title="getCtaTitle(cta)"
          :label="cta.text || ''"
          :variant="cta.type"
          :to="getCtaUrl(cta)"
          @click="handleCtaClick(cta)"
          :target="getCtaTarget(cta)"
        >
          <template v-if="cta.icon" #icon>
            <SharedPicture :media="cta.icon" width="24" height="24" format="webp" class="block !size-6 !min-w-6" :quality="80" />
          </template>
        </SharedButton>
      </div>
    </div>
    <ClientOnly>
      <component v-for="(component, i) in specialActionComponents" :is="component" :key="i" />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
  import { defineAsyncComponent } from 'vue';
  import type { CTAs, Button } from '~/types/strapi';
  import type { WithTypename } from '~/types/graphql';

  type CTAsComponent = WithTypename<CTAs>;

  const props = defineProps<{
    component: CTAsComponent;
  }>();

  const { t } = useI18n();

  const getCtaUrl = (cta: Button) => {
    if (cta.url) {
      return `${cta.url}${cta.target}`;
    }

    if (cta.target) {
      return `${cta.target}`;
    }

    if (cta.specialAction && cta.specialAction !== 'none') return '';

    if (cta.page) {
      return `/${cta.page.slug}`;
    }

    return '';
  };

  const getCtaTitle = (cta: Button) => {
    if (cta.specialAction && cta.specialAction !== 'none') {
      switch (cta.specialAction) {
        case 'buy_voucher':
          return t('components.ctas.buyVoucher');
        case 'book_test_drive':
          return t('components.ctas.bookTestDrive');
        case 'financing_modal':
          return t('components.ctas.financingModal');
        case 'book_service_modal':
          return t('components.ctas.bookServiceModal');
        default:
          return cta.text;
      }
    }

    if (cta.page) {
      return t('menu.navigateTo', { label: cta.page.title });
    }

    return cta.text;
  };

  const getCtaTarget = (cta: Button) => {
    if (cta.openInNewTab) {
      return '_blank';
    }

    return '_self';
  };

  const eventBus = useEventBus();

  const handleCtaClick = (cta: Button) => {
    if (cta.specialAction && cta.specialAction !== 'none') {
      if (cta.specialAction === 'buy_voucher') {
        eventBus.emit('open-buy-voucher-modal');
      }
      if (cta.specialAction === 'book_test_drive') {
        eventBus.emit('open-book-test-drive-modal');
      }
      if (cta.specialAction === 'financing_modal') {
        // !!! NOTE: The 3rd party this component uses is not working anymore. It is kept here for reference.
        // eventBus.emit('open-financing-modal');
      }
      if (cta.specialAction === 'book_service_modal') {
        eventBus.emit('open-book-service-modal');
      }
    }
  };

  const { getUniqueComponents, mountComponent } = useSpecialActionComponents();

  const specialActionComponents = computed(() => {
    if (!props.component.ctas?.length) return [];

    const specialActions = props.component.ctas
      .filter(cta => cta.specialAction && cta.specialAction !== 'none')
      .map(cta => cta.specialAction!)
      .filter(Boolean);

    // Only run on client to avoid hydration mismatch
    if (import.meta.client) {
      const uniqueActions = getUniqueComponents(specialActions);
      const components: any[] = [];

      uniqueActions.forEach(action => {
        switch (action) {
          // INFO: buy voucher modal is now always in the page
          // case 'buy_voucher':
          //   components.push(defineAsyncComponent(() => import('~/components/Shared/BuyVoucherModal/index.vue')));
          //   break;
          // INFO: book test drive is attached in the car detail component for clarity
          // case 'book_test_drive':
          //   components.push(defineAsyncComponent(() => import('~/components/Shared/BookTestDrive/index.vue')));
          //   break;
          default:
            break;
        }
      });

      return components;
    }

    return [];
  });
</script>
